/**
 * @file chatLocale.js
 * @description Maps the app's selected language code to a chat provider locale
 * (Zendesk Messaging / Intercom Messenger).
 *
 * The app's language list is dynamic (driven by the API), so we only need to
 * remap the handful of codes where ours differs from the provider's, pass everything
 * else through unchanged, and fall back to `en` for anything empty or unknown.
 *
 * Zendesk's supported locales: https://support.zendesk.com/api/v2/locales/public.json
 * Intercom's supported languages: https://www.intercom.com/help/en/articles/180-localize-intercom-to-work-with-multiple-languages
 */

const DEFAULT_LOCALE = 'en'

// Known mismatches between our language codes and Zendesk's locale codes.
// Keys are lowercased app codes; values are Zendesk locale codes.
const ZENDESK_LOCALE_OVERRIDES = {
  no: 'nb' // Norwegian → Zendesk uses Bokmål
}

/**
 * Convert an app language code to a Zendesk Messaging locale code.
 *
 * @param {string} [code] - App language code (e.g. 'en', 'de', 'no').
 * @returns {string} Zendesk locale code, defaulting to 'en'.
 */
export const mapToZendeskLocale = (code) => {
  if (!code) return DEFAULT_LOCALE
  const normalized = code.toLowerCase()
  return ZENDESK_LOCALE_OVERRIDES[normalized] || normalized
}

// Known mismatches between our language codes and Intercom's `language_override` codes.
// Keys are lowercased app codes; values are Intercom locale codes.
const INTERCOM_LOCALE_OVERRIDES = {
  gr: 'el', // Greek → ISO 639-1 'el'
  no: 'nb', // Norwegian → Intercom uses Bokmål (confirmed by Intercom support)
  jp: 'ja' // Japanese → our language code is 'JA'; guards tenants configured with the country code
}

/**
 * Convert an app language code to an Intercom Messenger locale code.
 *
 * @param {string} [code] - App language code (e.g. 'en', 'gr', 'no').
 * @returns {string} Intercom locale code, defaulting to 'en'.
 */
export const mapToIntercomLocale = (code) => {
  if (!code) return DEFAULT_LOCALE
  const normalized = code.toLowerCase()
  return INTERCOM_LOCALE_OVERRIDES[normalized] || normalized
}
